import React, {
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DirectionsRenderer,
  GoogleMap,
  Marker,
  Polyline,
} from '@react-google-maps/api';
import { toast } from 'react-toastify';
import { locationType } from '@src/types';
import { placesIconsStyles } from './constants';
import { createSingleMarker } from '@src/utils';
import { FullScreenOutwardIcon, FullScreenIcon } from '@src/svg';
import { MapsGate } from '@component/GoogleMap/MapsGate';

interface IRideRoute {
  height?: string;
  segmentsItemLocations: Array<locationType | null>;
  handleSetDistanceAndPath?: (value: any) => void;
  mapClassName?: string;
  suppressMarkers?: boolean;
  suppressPolylines?: boolean;
  showWaypointsAtCount?: number;
  showSingleWaypoint?: boolean;
  allowZoom?: boolean;
  preserveViewport?: boolean;
  clickableIcons?: boolean;
  ref?: any;
  onMapClick?: () => void;
  keyboardShortcuts?: boolean;
  withoutSpecialIcon?: boolean;
  dataTestId?: string;
  eventPoints?: Array<{
    lat: number;
    lng: number;
    color?: string;
    title?: string;
    deviation?: -1 | 0 | 1;
  }>;
}

const fixData = [
  {
    distance: { value: 0 },
    duration: { value: 0 },
  },
];

const RideRouteMap: FC<IRideRoute> = forwardRef(
  (
    {
      height = '270px',
      segmentsItemLocations,
      handleSetDistanceAndPath,
      mapClassName,
      suppressMarkers = false,
      suppressPolylines = false,
      showWaypointsAtCount = 2,
      allowZoom = false,
      preserveViewport = true,
      showSingleWaypoint = true,
      clickableIcons = false,
      onMapClick = () => null,
      keyboardShortcuts = false,
      withoutSpecialIcon = false,
      eventPoints = [],
      dataTestId = 'rd-route',
    },
    ref
  ) => {
    const [directions, setDirections] =
      useState<google.maps.DirectionsResult | null>(null);

    const [prevLocations, setPrevLocations] = useState<string>('');
    const [isFullscreen, setIsFullscreen] = useState(false);

    const mapRef = useRef<HTMLDivElement>(null);
    const googleMapRef = useRef<google.maps.Map | null>(null);
    const [currentZoom, setCurrentZoom] = useState<number>(14);

    const calculateRoute = useCallback(() => {
      const validLocations = segmentsItemLocations
        ?.map((location) => {
          if (location) {
            const { latitude, longitude } = location;
            return latitude && longitude
              ? { lat: +latitude, lng: +longitude }
              : null;
          }
          return null;
        })
        .filter(Boolean) as google.maps.LatLngLiteral[];

      if (validLocations.length < showWaypointsAtCount) {
        return;
      }

      const directionsService = new google.maps.DirectionsService();

      let waypoints: google.maps.DirectionsWaypoint[] = [];

      if (showWaypointsAtCount === 1 && validLocations.length === 2) {
        waypoints = [{ location: validLocations[1], stopover: true }];
      } else {
        waypoints = validLocations.slice(1, -1).map((location) => ({
          location,
          stopover: true,
        }));
      }

      const request: google.maps.DirectionsRequest = {
        origin: validLocations[0],
        destination: validLocations[validLocations.length - 1],
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          setDirections(result);
          if (result)
            handleSetDistanceAndPath &&
              handleSetDistanceAndPath([
                ...fixData,
                ...result?.routes?.[0]?.legs,
              ]);
        } else {
          toast.error(`Directions request failed: ${status}`);
        }
      });
    }, [segmentsItemLocations]);

    const mapOptions = useMemo(() => {
      const { latitude, longitude } = segmentsItemLocations?.[0] || {};

      const baseOptions = {
        zoom: 14,
        center: {
          lat: latitude ?? 34.0522,
          lng: longitude ?? -118.2437,
        },
        disableDefaultUI: !isFullscreen,
        clickableIcons,
        streetViewControl: isFullscreen,
        fullscreenControl: false,
        keyboardShortcuts,
        mapTypeControl: isFullscreen,
        rotateControl: true,
        scaleControl: true,
        styles: placesIconsStyles,
      };
      return allowZoom && isFullscreen
        ? {
            ...baseOptions,
            zoomControl: true,
            scrollwheel: true,
            gestureHandling: 'greedy',
            draggable: true,
          }
        : {
            ...baseOptions,
            zoomControl: false,
            scrollwheel: false,
            gestureHandling: 'none',
            draggable: false,
          };
    }, [
      segmentsItemLocations,
      allowZoom,
      isFullscreen,
      clickableIcons,
      keyboardShortcuts,
    ]);

    const locationsMarkers = useMemo(() => {
      if (
        showSingleWaypoint &&
        segmentsItemLocations.length === 1 &&
        Boolean(segmentsItemLocations[0])
      ) {
        return [
          createSingleMarker(segmentsItemLocations[0], withoutSpecialIcon),
        ];
      }
      return segmentsItemLocations.map((item) => item?.marker);
    }, [segmentsItemLocations]);

    const resetRoute = useCallback(() => {
      setDirections(null);
      handleSetDistanceAndPath && handleSetDistanceAndPath([]);
    }, [handleSetDistanceAndPath]);

    useImperativeHandle(ref, () => ({ resetRoute }));

    useEffect(() => {
      const currentLocations = JSON.stringify(segmentsItemLocations);
      if (
        currentLocations !== prevLocations &&
        segmentsItemLocations.length > 0
      ) {
        setPrevLocations(currentLocations);
        calculateRoute();
      }
    }, [segmentsItemLocations]);

    const polylinePath = useMemo(() => {
      return directions?.routes?.[0]?.overview_path || [];
    }, [directions]);

    const toggleFullscreen = () => {
      const el = mapRef.current;
      if (!el) return;

      if (document.fullscreenElement === el) {
        document.exitFullscreen?.();
      } else {
        el.requestFullscreen?.();
      }
    };

    const handleMapLoad = useCallback((map: google.maps.Map) => {
      googleMapRef.current = map;
      const zoom = map.getZoom();
      if (typeof zoom === 'number') setCurrentZoom(zoom);
    }, []);

    const handleZoomChanged = useCallback(() => {
      const zoom = googleMapRef.current?.getZoom();
      if (typeof zoom === 'number') setCurrentZoom(zoom);
    }, []);

    const adjustedEventPoints = useMemo(() => {
      const baseZoom = 14;
      const rawFactor = Math.pow(2, baseZoom - currentZoom);
      const isZoomOut = currentZoom < baseZoom;
      const zoomOutBoost = isZoomOut ? 1.7 : 1;
      const zoomFactor = Math.min(6, Math.max(0.45, rawFactor * zoomOutBoost));
      const latOffset = 0.000012 * zoomFactor;
      const lngOffset = 0.000028 * zoomFactor;
      const baseAdjusted = eventPoints.map((point, idx) => {
        const deviation = point.deviation ?? 0;
        if (!deviation) return { ...point, __idx: idx };

        return {
          ...point,
          lat: point.lat + -deviation * latOffset,
          lng: point.lng + deviation * lngOffset,
          __idx: idx,
        };
      });

      const groups = new Map<string, typeof baseAdjusted>();
      baseAdjusted.forEach((point) => {
        // Round to group truly overlapping coordinates together.
        const key = `${point.lat.toFixed(7)}:${point.lng.toFixed(7)}`;
        const group = groups.get(key) ?? [];
        group.push(point);
        groups.set(key, group);
      });

      const collisionFactor = Math.min(2.4, Math.max(0.9, rawFactor));
      const collisionLat = 0.00001 * collisionFactor;
      const collisionLng = 0.00002 * collisionFactor;

      groups.forEach((group) => {
        if (group.length <= 1) return;
        const startAngle = Math.PI / 4;
        group.forEach((point, index) => {
          const angle = startAngle + (2 * Math.PI * index) / group.length;
          point.lat += Math.sin(angle) * collisionLat;
          point.lng += Math.cos(angle) * collisionLng;
        });
      });

      return baseAdjusted.map(({ __idx, ...point }) => point);
    }, [eventPoints, currentZoom]);

    useEffect(() => {
      const handler = () => {
        setIsFullscreen(document.fullscreenElement === mapRef.current);
      };

      document.addEventListener('fullscreenchange', handler);
      return () => {
        document.removeEventListener('fullscreenchange', handler);
      };
    }, []);

    const customFullScreenIcon = (
      <button
        data-test-id={`${dataTestId}-fs`}
        onClick={toggleFullscreen}
        className="map_full_screen_icon"
      >
        {isFullscreen ? (
          <FullScreenIcon width={30} height={30} color={'#FFF'} />
        ) : (
          <FullScreenOutwardIcon width={30} height={30} color={'#FFF'} />
        )}
      </button>
    );

    return (
      <div
        data-test-id={`${dataTestId}-rd-route`}
        className={`ride-route-map ${mapClassName ?? ''}`}
        onClick={onMapClick}
        ref={mapRef}
        style={{ position: 'relative' }}
      >
        {customFullScreenIcon}
        <GoogleMap
          mapContainerStyle={{
            height: isFullscreen ? '100%' : height,
            width: '100%',
            borderRadius: 4,
            border: '2px solid #57568E',
          }}
          options={mapOptions}
          onLoad={handleMapLoad}
          onZoomChanged={handleZoomChanged}
        >
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers:
                  segmentsItemLocations.length === 1 ? true : suppressMarkers,
                suppressPolylines,
                preserveViewport,
              }}
            />
          )}

          {polylinePath.length > 0 && (
            <Polyline
              path={polylinePath}
              options={{
                strokeColor: '#4F7EFFFF',
                strokeOpacity: 1,
                strokeWeight: 4,
              }}
            />
          )}

          {locationsMarkers.length > 0 &&
            locationsMarkers.map((marker, index) => (
              <Marker
                key={index}
                position={marker?.position}
                label={marker?.label}
                icon={marker?.icon}
                title={marker?.title}
                options={{ clickable: false, draggable: false }}
              />
            ))}

          {adjustedEventPoints.map((point, idx) => (
            <Marker
              key={`${point.lat}-${point.lng}-${idx}`}
              position={{ lat: point.lat, lng: point.lng }}
              title={point.title}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                scale: 5,
                fillColor: point.color,
                fillOpacity: 1,
                strokeColor: point.color,
                strokeOpacity: 0,
                strokeWeight: 0,
              }}
              options={{ clickable: false, draggable: false }}
            />
          ))}
        </GoogleMap>
      </div>
    );
  }
);

/** Gated so a page with a route map still opens when Google Maps can't load. */
export const RideRoute: FC<IRideRoute> = forwardRef((props: IRideRoute, ref) => (
  <MapsGate height={props.height ?? '270px'}>
    <RideRouteMap {...props} ref={ref} />
  </MapsGate>
));
